import * as React from 'react';
import { useEffect, Fragment } from 'react';
import { 
  Box, 
  SwipeableDrawer, 
  Divider, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  ListItemButton,
  Typography,
  Avatar,
  Badge,
  Chip,
  Switch,
  styled,
  alpha
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { setAuthStatus } from '../request/Auth/authSlice';
import { useMutation } from "react-query";
import apiClient from '../request/http-common';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SendIcon from '@mui/icons-material/Send';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import CreateIcon from '@mui/icons-material/Create';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import ChatIcon from '@mui/icons-material/Chat';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ListIcon from '@mui/icons-material/List';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';


// Styled Components
const SidebarDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 280,
    background: 'linear-gradient(180deg, #0B0E11 0%, #161A1E 50%, #1E2328 100%)',
    border: 'none',
    borderRight: '1px solid #2B3139',
    overflowX: 'hidden'
  }
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: '24px 20px',
  background: 'linear-gradient(135deg, rgba(2, 192, 118, 0.1) 0%, rgba(2, 192, 118, 0.05) 100%)',
  borderBottom: '1px solid #2B3139',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '20px',
    right: '20px',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, #02C076 50%, transparent 100%)',
  }
}));

const UserProfile = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 0',
  cursor: 'pointer',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(2, 192, 118, 0.08)',
    transform: 'translateX(4px)'
  }
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: '48px',
  height: '48px',
  border: '2px solid #02C076',
  boxShadow: '0 4px 12px rgba(2, 192, 118, 0.3)'
}));

const MenuSection = styled(Box)(({ theme }) => ({
  padding: '16px 0',
  '&:not(:last-child)': {
    borderBottom: '1px solid rgba(43, 49, 57, 0.5)'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 700,
  color: '#6C7293',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  padding: '0 20px 12px 20px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '6px',
    left: '20px',
    width: '24px',
    height: '2px',
    background: 'linear-gradient(90deg, #02C076 0%, #00D4AA 100%)',
    borderRadius: '1px'
  }
}));

const MenuItem = styled(ListItemButton)(({ theme, active }) => ({
  margin: '4px 12px',
  borderRadius: '12px',
  padding: '12px 16px',
  minHeight: '48px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  
  ...(active && {
    background: 'linear-gradient(135deg, rgba(2, 192, 118, 0.15) 0%, rgba(2, 192, 118, 0.1) 100%)',
    border: '1px solid rgba(2, 192, 118, 0.3)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: 'linear-gradient(180deg, #02C076 0%, #00D4AA 100%)',
    }
  }),
  
  '&:hover': {
    background: active 
      ? 'linear-gradient(135deg, rgba(2, 192, 118, 0.2) 0%, rgba(2, 192, 118, 0.15) 100%)'
      : 'rgba(255, 255, 255, 0.05)',
    transform: 'translateX(4px)',
    '& .menu-icon': {
      transform: 'scale(1.1)',
      color: '#02C076'
    },
    '& .menu-arrow': {
      transform: 'translateX(4px)',
      opacity: 1
    }
  }
}));

const MenuIcon = styled(Box)(({ theme, active }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: active 
    ? 'linear-gradient(135deg, rgba(2, 192, 118, 0.2) 0%, rgba(2, 192, 118, 0.1) 100%)'
    : 'rgba(255, 255, 255, 0.05)',
  border: `1px solid ${active ? 'rgba(2, 192, 118, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
  color: active ? '#02C076' : '#B7BDC6',
  transition: 'all 0.3s ease',
  marginRight: '12px'
}));

const MenuText = styled(Typography)(({ theme, active }) => ({
  fontSize: '14px',
  fontWeight: active ? 600 : 500,
  color: active ? '#FFFFFF' : '#B7BDC6',
  transition: 'color 0.3s ease'
}));

const MenuArrow = styled(ChevronRightIcon)(({ theme }) => ({
  fontSize: '18px',
  color: '#6C7293',
  transition: 'all 0.3s ease',
  opacity: 0,
  marginLeft: 'auto'
}));

const StatusBadge = styled(Chip)(({ theme }) => ({
  height: '20px',
  fontSize: '10px',
  fontWeight: 600,
  background: 'linear-gradient(135deg, #02C076 0%, #05E085 100%)',
  color: '#FFFFFF',
  border: 'none',
  '& .MuiChip-label': {
    padding: '0 8px'
  }
}));

export default function SideBar() {
  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  const appMode = useSelector((state) => state.mode.mode);
  const isAuth = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.user.user);
  const userRole = useSelector((state) => state.user.currentRole);
  const notifications = useSelector((state) => state.notifications.notifications);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading: isLoggingOut, mutate: postLogout } = useMutation(
    async () => {
      return await apiClient.post(`/api/logout`);
    },
    {
      onSuccess: (res) => {
        if (res.data.message === 'successful') {
          dispatch(setAuthStatus('unauthenticated'));
          localStorage.removeItem('user');
          localStorage.removeItem('fundings');
          localStorage.setItem('funding-balance', 0);
          localStorage.setItem('subscriptionBalance', 0);
          localStorage.removeItem('subscriptions');
          localStorage.removeItem('notifications');
          localStorage.removeItem('auth-token');
          
          const event = new Event('userIsLoggedOut');
          window.dispatchEvent(event);
          navigate('/login');
        }            
      },
      onError: (err) => {
        console.log(err.response?.data || err);
      },
    }
  );

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setState({ ...state, [anchor]: open });
  };

  useEffect(() => {
    window.addEventListener(
      "toggleSideBar",
      (e) => {
       setState({ ...state, ['left']: true });
      },
      false
    );
    // Listen for closeSideBar event
    window.addEventListener(
      "closeSideBar",
      () => {
        setState((prev) => ({ ...prev, left: false }));
      },
      false
    );
  }, []);

  const tradingMenuItems = [
    { path: '/', icon: HomeIcon, label: 'Dashboard', hasNotification: false },
    { path: '/start-trading', icon: TrendingUpIcon, label: 'Trade', hasNotification: false },
    { path: '/copy-plans', icon: ContentCopyIcon, label: 'Copy Trading', hasNotification: false },
    { path: '/package/Trading Bots', icon: SmartToyIcon, label: 'Trading Bots', hasNotification: false }
  ];

  const walletMenuItems = [
    { path: '/assets', icon: AccountBalanceWalletIcon, label: 'Assets', hasNotification: false },
    { path: '/account-wallet', icon: ShowChartIcon, label: 'Transactions', hasNotification: false },
    { path: '/send-coin', icon: SendIcon, label: 'Withdraw', hasNotification: false },
    { path: '/swap-coin', icon: SwapHorizIcon, label: 'Swap', hasNotification: false }
  ];

  const historyMenuItems = [
    { path: '/trade-history', icon: HistoryIcon, label: 'Trade History', hasNotification: false },
    { path: '/copy-history', icon: ContentCopyIcon, label: 'Copy History', hasNotification: false },
    { path: '/investments', icon: ShowChartIcon, label: 'Investments', hasNotification: false }
  ];

  const accountMenuItems = [
    { path: '/profile', icon: PersonIcon, label: 'Profile', hasNotification: false },
    { path: '/profile', icon: SettingsIcon, label: 'Settings', hasNotification: false },
    { path: '/notifications', icon: NotificationsIcon, label: 'Notifications', hasNotification: true, count: notifications?.length || 0 },
    { path: '/help', icon: HelpOutlineIcon, label: 'Help & Support', hasNotification: false }
  ];

  // Admin menu items - exact copy from desktop Admin.js
  const adminMenuItems = [
    { path: '/profile', icon: AccountBoxIcon, label: 'Profile', hasNotification: false },
    { path: '/list-users', icon: PeopleIcon, label: 'users', hasNotification: false },
    { path: '/create-plans', icon: CreateIcon, label: 'Create Investment Plan', hasNotification: false },
    { path: '/create-trading-plans', icon: CreateIcon, label: 'Create Trading Plan', hasNotification: false },
    { path: '/create-trader', icon: CreateIcon, label: 'Create Copy Traders', hasNotification: false },
    { path: '/create-trades', icon: CreateIcon, label: 'Create Copy Trades', hasNotification: false },
    { path: '/list-copiers', icon: ListIcon, label: 'List Copyings (investments)', hasNotification: false },
    { path: '/list-traders', icon: ListIcon, label: 'List Copy Traders', hasNotification: false },
    { path: '/list-trades', icon: ListIcon, label: 'List Copy Trades', hasNotification: false },
    { path: '/create-address', icon: CreateIcon, label: 'Create Wallets', hasNotification: false },
    { path: '/list-trading-plans', icon: ListIcon, label: 'List Trading Plans', hasNotification: false },
    { path: '/list-fundings', icon: ListIcon, label: 'List Deposits', hasNotification: false },
    { path: '/list-investments', icon: ListIcon, label: 'List Investments', hasNotification: false },
    { path: '/list-referrals', icon: ListIcon, label: 'List Referrals', hasNotification: false },
    { path: '/list-cards', icon: ListIcon, label: 'List Cards', hasNotification: false },
    { path: '/list-plans', icon: ListIcon, label: 'Investment Plans', hasNotification: false },
    { path: '/list-address', icon: ListIcon, label: 'List Payment Wallets', hasNotification: false },
    { path: '/list-withdrawals', icon: MoneyOffIcon, label: 'Withdrawals', hasNotification: false },
    { path: '/send-mail', icon: EmailIcon, label: 'send email', hasNotification: false },
    { path: '/create-message', icon: EmailIcon, label: 'create message', hasNotification: false },
    { path: '/live-chats', icon: ChatIcon, label: 'Live Chat', hasNotification: false }
  ];

  const renderMenuItem = (item) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    // Special handling for Help & Support to trigger Smartsupp chat
    if (item.path === '/help') {
      return (
        <MenuItem
          key={item.path}
          active={isActive}
          onClick={() => {
            toggleDrawer('left', false)();
            if (window.smartsupp) {
              window.smartsupp('chat:open');
            }
          }}
        >
          <MenuIcon active={isActive} className="menu-icon">
            <Icon sx={{ fontSize: '20px' }} />
          </MenuIcon>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <MenuText active={isActive}>{item.label}</MenuText>
            {item.hasNotification && (
              <Badge 
                badgeContent={item.count || 0} 
                sx={{ 
                  ml: 1,
                  '& .MuiBadge-badge': {
                    background: 'linear-gradient(135deg, #F84960 0%, #FF6B85 100%)',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 600,
                    minWidth: '18px',
                    height: '18px'
                  }
                }}
              />
            )}
          </Box>
          
          <MenuArrow className="menu-arrow" />
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={item.path}
        component={RouterLink}
        to={item.path}
        active={isActive}
        onClick={toggleDrawer('left', false)}
      >
        <MenuIcon active={isActive} className="menu-icon">
          <Icon sx={{ fontSize: '20px' }} />
        </MenuIcon>
        
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <MenuText active={isActive}>{item.label}</MenuText>
          {item.hasNotification && (
            <Badge 
              badgeContent={item.count || 0} 
              sx={{ 
                ml: 1,
                '& .MuiBadge-badge': {
                  background: 'linear-gradient(135deg, #F84960 0%, #FF6B85 100%)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600,
                  minWidth: '18px',
                  height: '18px'
                }
              }}
            />
          )}
        </Box>
        
        <MenuArrow className="menu-arrow" />
      </MenuItem>
    );
  };

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <SidebarHeader>
        {isAuth && user.name ? (
          <UserProfile component={RouterLink} to="/profile" onClick={toggleDrawer('left', false)}>
            <UserAvatar src={user.profile} alt={user.name}>
              {user.name?.charAt(0)}
            </UserAvatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#FFFFFF', mb: 0.5 }}>
                {user.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StatusBadge label="Online" />
                <Typography sx={{ fontSize: '12px', color: '#6C7293' }}>
                  {userRole || 'Member'}
                </Typography>
              </Box>
            </Box>
          </UserProfile>
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#FFFFFF', mb: 1 }}>
              Welcome
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#6C7293' }}>
              Please sign in to continue
            </Typography>
          </Box>
        )}
      </SidebarHeader>

      {/* Menu Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        {isAuth && user.name ? (
          <>
            {userRole === 'admin' ? (
              <MenuSection>
                <SectionTitle>Admin</SectionTitle>
                {adminMenuItems.map(renderMenuItem)}
              </MenuSection>
            ) : (
              <>
                <MenuSection>
                  <SectionTitle>Trading</SectionTitle>
                  {tradingMenuItems.map(renderMenuItem)}
                </MenuSection>

                <MenuSection>
                  <SectionTitle>Wallet</SectionTitle>
                  {walletMenuItems.map(renderMenuItem)}
                </MenuSection>

                <MenuSection>
                  <SectionTitle>History</SectionTitle>
                  {historyMenuItems.map(renderMenuItem)}
                </MenuSection>

                <MenuSection>
                  <SectionTitle>Account</SectionTitle>
                  {accountMenuItems.map(renderMenuItem)}
                </MenuSection>
              </>
            )}
          </>
        ) : (
          <MenuSection>
            <MenuItem component={RouterLink} to="/login" onClick={toggleDrawer('left', false)}>
              <MenuIcon className="menu-icon">
                <PersonIcon sx={{ fontSize: '20px' }} />
              </MenuIcon>
              <MenuText>Sign In</MenuText>
              <MenuArrow className="menu-arrow" />
            </MenuItem>
          </MenuSection>
        )}
      </Box>

      {/* Footer */}
      {isAuth && user.name && (
        <Box sx={{ p: 2, borderTop: '1px solid #2B3139' }}>
          <MenuItem onClick={postLogout}>
            <MenuIcon className="menu-icon">
              <LogoutIcon sx={{ fontSize: '20px' }} />
            </MenuIcon>
            <MenuText>{isLoggingOut ? 'Signing Out...' : 'Sign Out'}</MenuText>
            <MenuArrow className="menu-arrow" />
          </MenuItem>
        </Box>
      )}
    </Box>
  );

  return (
    <SidebarDrawer
            sx={{ zIndex: 25000 }}
      anchor="left"
      open={state.left}
      onClose={toggleDrawer('left', false)}
      onOpen={toggleDrawer('left', true)}
    >
      {sidebarContent}
    </SidebarDrawer>
  );
}